import { getLogger } from '../../diagnostics/logger';
import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  DELETE_USER_SETTINGS,
  ENSURE_USER_SETTINGS,
  INITIAL_AUTHENTICATION,
  LOAD_USER_SETTINGS,
  PUT_USER_SETTINGS,
} from '../../actions/actionTypes';
import { Action, PromiseAction } from '../../actions/commonActions';
import { addNotification } from '../../actions/notificationActions';
import {
  deleteUserSettingsFailure,
  deleteUserSettingsSuccess,
  loadUserSettings,
  loadUserSettingsFailure,
  loadUserSettingsPending,
  loadUserSettingsSuccess,
  putUserSettingsFailure,
  putUserSettingsSuccess,
} from '../../actions/userSettingsActions';
import * as userSettingsApi from '../../api/userSettingsApi';
import { Loadable } from '../../models';
import { SimManagementError, getErrorState } from '../../models/Errors';
import { UserSettings } from '../../models/state/userSettings';
import { selectUserSettings } from '../../selectors/userSettings';
import { applyThemeSettings, clearThemeSettings } from '../../services/portalBranding';

const log = getLogger('simmanagement.sagas.login');

function getRouteThemeContext(pathname: string): { companyId: number | null; accountId: number | null } {
  const match = pathname.match(/\/company\/(\d+)(?:\/account\/(\d+))?/);

  if (!match) {
    return { companyId: null, accountId: null };
  }

  return {
    companyId: match[1] ? parseInt(match[1], 10) : null,
    accountId: match[2] ? parseInt(match[2], 10) : null,
  };
}

/**
 * A saga which deletes user settings from the server.
 * @param action
 */
export function* deleteUserSettingsSaga(action: PromiseAction<UserSettings, UserSettings>) {
  if (!action.payload) {
    log.warn(`Attempted to process action ${action.type} without payload. Ignoring.`);
    action.reject && action.reject('No payload.');
    return;
  }

  try {
    const userSettings = action.payload;
    yield call(userSettingsApi.remove, userSettings.userId);
    yield put(deleteUserSettingsSuccess(userSettings));
    action.resolve && action.resolve(userSettings);
  } catch (error: any) {
    const err = error as SimManagementError;
    put(deleteUserSettingsFailure(err, action.payload));
    yield put(
      addNotification({
        category: 'task',
        title: 'User Settings Reset Failed',
        detail: (err.validationErrors && err.validationErrors[''][0]) || 'An error occurred',
        type: 'error',
        showInCount: false,
      })
    );

    action.reject && action.reject(err);
  }
}

/**
 * Ensure that user settings are present in the store and will initiate a server load if not.
 * @param action The action that invoked the saga.
 */
export function* ensureUserSettingsSaga(action: Action<string>) {
  if (!action.payload) {
    log.warn(`Attempted to process action ${action.type} without payload. Ignoring.`);
    return;
  }

  const userSettings: Loadable<UserSettings> = yield select(selectUserSettings);
  const id = action.payload;
  if (userSettings.userId !== id) {
    yield put(loadUserSettings(id));
  }
}

/**
 * Load the user settings.
 * @param action The action that invoked the saga.
 */
export function* loadUserSettingsSaga(action: Action<string>) {
  if (!action.payload) {
    log.warn(`Attempted to process action ${action.type} without payload. Ignoring.`);
    return;
  }

  const id = action.payload;
  yield put(loadUserSettingsPending(id));
  try {
    const userSettings: UserSettings = yield call(userSettingsApi.getById, id);
    try {
      const routeThemeContext =
        typeof window !== 'undefined'
          ? getRouteThemeContext(window.location.pathname)
          : { companyId: null, accountId: null };
      const themeCompanyId = routeThemeContext.companyId ?? userSettings.companyId ?? null;
      const themeAccountId = routeThemeContext.accountId ?? userSettings.accountId ?? null;
      const themeSettings: Record<string, string> | null = yield call(
        userSettingsApi.getTheme,
        userSettings.userId,
        themeCompanyId,
        themeAccountId
      );
      if (themeSettings != null) {
        applyThemeSettings(themeSettings);
      } else {
        clearThemeSettings();
      }
    } catch (err: any) {
    }

    yield put(loadUserSettingsSuccess(userSettings));
  } catch (error: any) {
    yield put(loadUserSettingsFailure(id, error));
  }
}

/**
 * Saga which contains logic for putting user settings to the server.
 * @param action The action which invoked the saga.
 */
export function* putUserSettingsSaga(action: PromiseAction<UserSettings, UserSettings>) {
  if (!action.payload) {
    log.warn('Attempted to put user settings to the server but no payload passed in. Ignored.');
    action.reject && action.reject('No payload.');
    return;
  }

  const userSettings = action.payload;
  try {
    const response = (yield call(userSettingsApi.put, userSettings)) as UserSettings;
    yield put(putUserSettingsSuccess(response));
    yield put(
      addNotification({
        category: 'task',
        title: 'User Settings Saved',
        detail: `User settings were saved successfully.`,
        type: 'success',
        showInCount: false,
      })
    );

    action.resolve && action.resolve(response);
  } catch (error: any) {
    const err = error as SimManagementError;
    yield put(putUserSettingsFailure(err, action.payload));
    const errorState = getErrorState(err.validationErrors);
      yield put(
        addNotification({
          category: 'task',
          title: 'User Settings Save Failed',
          detail: (errorState.isValid && err.validationErrors && err.validationErrors[''][0]) || err.message || 'An error occurred',
          type: 'error',
          showInCount: false,
        })
      );

    action.reject && action.reject(err);
  }
}

/*
 * Initializes all sagas that are related to user settings.
 */
export function* initUserSettingsSagas() {
  yield take(INITIAL_AUTHENTICATION);
  yield takeEvery(DELETE_USER_SETTINGS, deleteUserSettingsSaga);
  yield takeEvery(PUT_USER_SETTINGS, putUserSettingsSaga);
  yield takeEvery(ENSURE_USER_SETTINGS, ensureUserSettingsSaga);
  yield takeEvery(LOAD_USER_SETTINGS, loadUserSettingsSaga);
}
